import {
  customerSessionSchema,
  IPublicSession,
  repushVoipMasters,
  sharedSessionSchema,
  userSession,
  userSessionSchema,
} from 'state/session';
import { user } from 'utils/auth/user';
import { getVoipSlotId } from 'utils/voipTabId';
import { getLastSeenVersion, seedRemoteVersion } from './remoteVersion';
import { runApplyingRemote } from './remoteApplyGuard';
import { mergeVoipMasters, IVoipMastersMergeResult, TVoipMasterSlot } from './voipClaim';
import { clearDirty, clearDirtyEntry, getDirtyEntryFields } from './dirtyKeys';
import {
  sessionVehicleSchema,
  type ISessionVehicle,
  type IVehicle,
  type IVehicleId,
  type TPrString,
} from 'data/schema';
import { holdDeckPrString } from './deckPrStringHold';
import { isEqual } from 'utils/deepEqual';
import logger from 'utils/log';

/**
 * The single entry point for applying session state that came from the server — WebSocket
 * broadcasts and the reconnect read-repair GET funnel through here (AVS-4330). Keeping one
 * chokepoint means the staleness gate, the schema strip, and dirty-key protection cannot be
 * bypassed by a new call site. (The dev-only cross-tab mirror in `localSync.ts` applies state
 * on its own path with its own guard and does NOT route through here.)
 */

/**
 * Sink for the Session Flight Recorder's inbound half of the `sync` lane (AVS-4429).
 *
 * Same discipline and the same reason as `setSyncDecisionSink` in `syncToServer.ts`: a settable
 * module-level function defaulting to a no-op, called from inside the owner and isolated in its own
 * try/catch, so the recorder never has to be imported from app code (which would drag it past the
 * build gate into the prod artefact) and never has to register a `change` listener.
 *
 * @param event - what happened here, e.g. `in:drop-stale-version`, `in:applied`
 * @param detail - the branch's evidence; live references are fine, the recorder copies immediately
 */
export type TRemoteApplySink = (event: string, detail: Record<string, unknown>) => void;

function noopRemoteApplySink(): void {
  // Default sink: nothing is recording, and each inbound payload pays one no-op call.
}

let remoteApplySink: TRemoteApplySink = noopRemoteApplySink;

/** Register the recorder's inbound lane. Pass `null` to restore the no-op. */
export function setRemoteApplySink(sink: TRemoteApplySink | null): void {
  remoteApplySink = sink ?? noopRemoteApplySink;
}

/**
 * True when someone is listening. Read before assembling the fingerprint and the strip list, which
 * are the only parts of this instrumentation that cost more than an object literal.
 */
function isRemoteApplyRecorded(): boolean {
  return remoteApplySink !== noopRemoteApplySink;
}

function reportRemoteApply(event: string, detail: Record<string, unknown>): void {
  try {
    remoteApplySink(event, detail);
  } catch (error) {
    console.error('[Session] remote-apply sink threw and was isolated', error);
  }
}

/** What a received payload's key set says about who wrote it. */
export type TRemoteWriterShape = 'dealer' | 'customer-shaped' | 'unknown';

export interface IRemoteWriterFingerprint {
  /** The payload's key set, sorted — stable across clients, so identical writers group. */
  keys: string[];
  keyCount: number;
  shape: TRemoteWriterShape;
  /** Keys only a dealer's PUT body can carry. Non-empty means: definitely a dealer write. */
  dealerOnlyKeys: string[];
}

/**
 * Keys the SERVER adds to a broadcast that no writer put in its body. `version` rides inside `data`
 * (`lambda/session/SessionService.ts:211`) precisely so receivers can order payloads, and it is
 * stripped from every PUT body by `sharedSessionSchema`, so it says nothing about the author.
 */
const SERVER_ADDED_KEYS: ReadonlySet<string> = new Set(['version']);

/** Computed once: `shared \ customer`, i.e. the keys a customer's PUT body can never carry. */
let dealerOnlyKeyCache: ReadonlySet<string> | null = null;
let customerKeyCache: ReadonlySet<string> | null = null;

function customerKeySet(): ReadonlySet<string> {
  if (!customerKeyCache) {
    customerKeyCache = new Set(Object.keys(customerSessionSchema.shape));
  }

  return customerKeyCache;
}

function dealerOnlyKeySet(): ReadonlySet<string> {
  if (!dealerOnlyKeyCache) {
    const customerKeys = customerKeySet();
    dealerOnlyKeyCache = new Set(Object.keys(sharedSessionSchema.shape).filter((key) => !customerKeys.has(key)));
  }

  return dealerOnlyKeyCache;
}

/**
 * Attribute a received payload to a dealer or a customer from its key set alone (AVS-4429, AC-4).
 *
 * `emitter=<connectionId>` is on the PUT URL (`state/session.ts:382`) but is NOT in the broadcast —
 * `SessionService.notifyParticipants` (`:184-214`) uses it only to filter recipients — so it is
 * visible only from the writer's own tab. With every participant recording, the merge CLI has the
 * author directly; this is the fallback for partial coverage, and it works because the broadcast
 * payload IS the writer's own (sanitized) body: a customer's is built from `customerSessionSchema`
 * and a dealer's from `sharedSessionSchema` (`state/session.ts:305-310`).
 *
 * Fingerprints the key **set**, never the count. 41 keys vs 42 is `prString`, which the server
 * deliberately strips from broadcasts unless `updateEverything` is set (`SessionService.ts:197-203`)
 * — the same author, not a different one. Because `prString` is only ONE of the dealer-only keys,
 * a stripped broadcast still classifies correctly on the rest.
 *
 * Customers write sessions of their own (AVS-4412), so `customer-shaped` is a real answer here and
 * not a synonym for "nobody".
 */
export function fingerprintRemoteWriter(raw: Record<string, unknown>): IRemoteWriterFingerprint {
  const keys = Object.keys(raw).sort();
  const authored = keys.filter((key) => !SERVER_ADDED_KEYS.has(key));
  const dealerOnly = dealerOnlyKeySet();
  const dealerOnlyKeys = authored.filter((key) => dealerOnly.has(key));

  const customerKeys = customerKeySet();
  const shape: TRemoteWriterShape =
    dealerOnlyKeys.length > 0
      ? 'dealer'
      : authored.length > 0 && authored.every((key) => customerKeys.has(key))
        ? 'customer-shaped'
        : 'unknown';

  return { keys, keyCount: keys.length, shape, dealerOnlyKeys };
}

export interface IApplyRemoteOptions {
  /**
   * Keys with unflushed local edits. A remote payload never overwrites these — otherwise a
   * reconnect read-repair (which legitimately carries a newer version) would silently discard
   * a pending local change that has not reached the server yet.
   */
  dirtyKeys?: Iterable<keyof IPublicSession>;
}

const sameVoipMasters = (a: Record<string, TVoipMasterSlot>, b: Record<string, TVoipMasterSlot>): boolean => {
  const aKeys = Object.keys(a);
  return (
    aKeys.length === Object.keys(b).length &&
    aKeys.every(
      (key) => b[key] !== undefined && a[key].tabId === b[key].tabId && (a[key].gen ?? 0) === (b[key].gen ?? 0)
    )
  );
};

/** The active vehicle id this payload leaves in place: its own, else the one the tab holds. */
function appliedActiveVehicleId(applied: Record<string, unknown>): IVehicleId | undefined {
  return ('activeVehicleId' in applied ? applied.activeVehicleId : userSession().get('activeVehicleId')) as
    IVehicleId | undefined;
}

const SESSION_VEHICLE_KEYS = Object.keys(sessionVehicleSchema.shape) as (keyof ISessionVehicle)[];

/**
 * Keep the active vehicle entry in step with root when a pending local edit held `vehicles` back
 * while the payload's root car fields still applied (AVS-4470). `updateVehicle` spreads the active
 * entry over root, so a stale entry would roll root back on the next write.
 *
 * A different car at the active id supersedes the local entry and the pending edits root still
 * holds for the car it replaced; those keys take the new entry's values and drop their dirty
 * marker (the same rule AVS-4399 applies to `carImages`). The same car keeps its pending fields
 * and takes the rest from the remote entry. A pending field is one the entry's own edit changed,
 * or a dirty root key while this tab already had that entry active (root follows only the active
 * entry). A pending local car change (on root or on the entry) or activation keeps the whole local
 * registry. Writes into `applied`.
 */
function adoptHeldBackActiveEntry(
  applied: Record<string, unknown>,
  remoteVehicles: Record<IVehicleId, IVehicle> | undefined,
  rawVehicles: Record<IVehicleId, object> | undefined,
  dirtyKeys: ReadonlySet<keyof IPublicSession>
): void {
  const activeVehicleId = appliedActiveVehicleId(applied);
  if (
    !remoteVehicles ||
    !activeVehicleId ||
    dirtyKeys.has('prString') ||
    dirtyKeys.has('activeVehicleId') ||
    getDirtyEntryFields(activeVehicleId).has('prString')
  ) {
    return;
  }
  const remoteEntry = remoteVehicles[activeVehicleId];
  if (!remoteEntry) {
    return;
  }
  const localVehicles = userSession().get('vehicles');
  const localEntry = localVehicles?.[activeVehicleId];
  const wasLocallyActive = activeVehicleId === userSession().get('activeVehicleId');
  let entry: IVehicle;
  if (!localEntry || localEntry.prString !== remoteEntry.prString) {
    entry = remoteEntry;
    const superseded = SESSION_VEHICLE_KEYS.filter((key) => dirtyKeys.has(key));
    superseded.forEach((key) => {
      applied[key] = remoteEntry[key];
    });
    clearDirty(superseded);
    clearDirtyEntry(activeVehicleId);
  } else {
    const pendingFields = getDirtyEntryFields(activeVehicleId);
    // Presence comes from the raw entry: the parser fills `.default()` keys the sender never sent.
    const carried = rawVehicles?.[activeVehicleId] ?? {};
    const remoteFields: Partial<ISessionVehicle> = {};
    SESSION_VEHICLE_KEYS.forEach((key) => {
      const pending = pendingFields.has(key) || (wasLocallyActive && dirtyKeys.has(key));
      if (!pending && key in carried) {
        Object.assign(remoteFields, { [key]: remoteEntry[key] });
      }
    });
    entry = { ...localEntry, ...remoteFields };
    if (isEqual(entry, localEntry)) {
      return;
    }
  }
  applied.vehicles = { ...localVehicles, [activeVehicleId]: entry };
}

/**
 * The payload's own active entry configuration, when it carried the registry but no root `prString`
 * (AVS-4462). The server strips root `prString` from 3D broadcasts, so a tab whose deck misses the
 * AVE car update would otherwise keep the previous car. Pending local car edits and comparison mode
 * keep root, the same way `activateVehicle` and the dirty-key gate do.
 */
function prStringFromActiveEntry(
  rawRecord: Record<string, unknown>,
  remoteVehicles: Record<IVehicleId, IVehicle> | undefined,
  applied: Record<string, unknown>,
  dirtyKeys: ReadonlySet<keyof IPublicSession>
): TPrString | undefined {
  if ('prString' in rawRecord || !remoteVehicles || dirtyKeys.has('prString') || dirtyKeys.has('activeVehicleId')) {
    return undefined;
  }
  const activeVehicleId = appliedActiveVehicleId(applied);
  const comparisonMode = 'comparisonMode' in applied ? applied.comparisonMode : userSession().get('comparisonMode');
  if (!activeVehicleId || comparisonMode === 'on' || getDirtyEntryFields(activeVehicleId).has('prString')) {
    return undefined;
  }
  const prString = remoteVehicles[activeVehicleId]?.prString;
  return prString && prString !== userSession().get('prString') ? prString : undefined;
}

/**
 * The derived root drops a gallery for another car (below), so the active entry must not keep one
 * either, or the next `updateVehicle` spreads the entry back over root and restores it. When root
 * holds the new car's gallery, the entry takes that instead, the way `broadcastCarFields` does (AVS-4462).
 */
function reconcileEntryGallery(applied: Record<string, unknown>, prString: TPrString): void {
  const vehicles = applied.vehicles as Record<IVehicleId, IVehicle> | undefined;
  const activeVehicleId = appliedActiveVehicleId(applied);
  const entry = activeVehicleId ? vehicles?.[activeVehicleId] : undefined;
  if (!activeVehicleId || !entry?.carImages || entry.carImages.prString === prString) {
    return;
  }
  const rootGallery = ('carImages' in applied ? applied.carImages : userSession().get('carImages')) as
    IVehicle['carImages'] | undefined;
  const gallery: Pick<IVehicle, 'carImages' | 'carImagesStatus'> =
    rootGallery?.prString === prString
      ? {
          carImages: rootGallery,
          carImagesStatus: ('carImagesStatus' in applied
            ? applied.carImagesStatus
            : userSession().get('carImagesStatus')) as IVehicle['carImagesStatus'],
        }
      : { carImages: undefined, carImagesStatus: 'unset' };
  applied.vehicles = { ...vehicles, [activeVehicleId]: { ...entry, ...gallery } };
}

/**
 * @returns true when the payload was applied, false when it was dropped or was a no-op.
 * (A dropped payload may still contribute its `voipMasters` slice — see below.)
 */
export function applyRemoteSession(raw: unknown, options: IApplyRemoteOptions = {}): boolean {
  if (!raw || typeof raw !== 'object') {
    reportRemoteApply('in:drop-not-an-object', { received: typeof raw });
    return false;
  }

  const rawRecord = raw as Record<string, unknown>;
  const incomingVersion = typeof rawRecord.version === 'number' ? rawRecord.version : undefined;
  const lastSeenVersion = getLastSeenVersion();
  // The fingerprint is the only part of this instrumentation that costs more than an object
  // literal, so it is assembled only when something is actually listening.
  const writer = isRemoteApplyRecorded() ? fingerprintRemoteWriter(rawRecord) : undefined;

  // voipMasters is self-reconciling (per-slot gen ordering — voipClaim.ts), so it is merged
  // gen-aware OUTSIDE the version gate and the dirty-skip: either gate arbitrates with weaker
  // information than the key's own ordering and could drop or revert a takeover broadcast.
  let voipMerge: IVoipMastersMergeResult | null = null;
  if (rawRecord.voipMasters !== undefined) {
    const slice = userSessionSchema.shape.voipMasters.safeParse(rawRecord.voipMasters);
    if (slice.success) {
      voipMerge = mergeVoipMasters(
        userSession().get('voipMasters'),
        slice.data,
        user.userPseudonym || undefined,
        getVoipSlotId(userSession().get('id'))
      );
    }
  }

  // Applies the merged map when it changes anything, and repairs the server when our own
  // newer claim won — the last-writer-wins server may still hold the losing map.
  const applyVoipMergeAndRepair = () => {
    if (!voipMerge) return;
    const merged = voipMerge.merged;
    if (!sameVoipMasters(userSession().get('voipMasters') ?? {}, merged)) {
      runApplyingRemote(() => userSession().setValues({ voipMasters: merged }));
    }
    if (voipMerge.ownSlotDefended) {
      repushVoipMasters();
    }
  };

  // Drop anything strictly older than what we already have. Deliberately `<` and not `<=`:
  // an emitOnly broadcast echoes the sender's CURRENT version without advancing it, so `<=`
  // would drop every legitimate emitOnly update. A payload with no version at all (an older
  // server, or session-end) cannot be ordered, so it is applied — preserving prior behaviour.
  if (incomingVersion !== undefined && lastSeenVersion !== undefined && incomingVersion < lastSeenVersion) {
    logger.info('[Session] Dropping stale remote payload', { incomingVersion, lastSeenVersion });
    reportRemoteApply('in:drop-stale-version', { incomingVersion, lastSeenVersion, writer });
    applyVoipMergeAndRepair();
    return false;
  }

  const parsed = userSessionSchema.partial().strip().safeParse(rawRecord);
  if (!parsed.success) {
    logger.warn('[Session] Rejecting malformed remote payload', parsed.error?.message);
    reportRemoteApply('in:drop-malformed', { incomingVersion, writer, reason: parsed.error?.message });
    applyVoipMergeAndRepair();
    return false;
  }

  // Apply ONLY keys the payload actually carried. A schema parse materialises a value for
  // every key that has a `.default()` (voipMasters -> {}, streamActive -> true, ...), so
  // applying `parsed.data` wholesale would stomp local state with defaults the server never
  // sent — reintroducing the very clobbering this gate exists to stop.
  const dirtyKeys = new Set<keyof IPublicSession>(options.dirtyKeys ?? []);
  const applied: Record<string, unknown> = {};
  /** Keys the payload carried that a pending local edit outranked — the AVS-4330 dirty-key gate. */
  const dirtySkipped: string[] = [];

  Object.keys(parsed.data).forEach((key) => {
    if (!(key in rawRecord)) {
      return;
    }
    if (key === 'voipMasters' && voipMerge) {
      return; // handled by the gen-aware merge below
    }
    if (dirtyKeys.has(key as keyof IPublicSession)) {
      logger.info('[Session] Keeping unflushed local edit over remote value', { key });
      dirtySkipped.push(key);
      return;
    }
    applied[key] = (parsed.data as Record<string, unknown>)[key];
  });

  if (voipMerge && !sameVoipMasters(userSession().get('voipMasters') ?? {}, voipMerge.merged)) {
    applied.voipMasters = voipMerge.merged;
  }

  if (dirtySkipped.includes('vehicles')) {
    // The parse succeeded, so the raw `vehicles` is the record the parsed one came from.
    const rawVehicles = rawRecord.vehicles as Record<IVehicleId, object> | undefined;
    adoptHeldBackActiveEntry(applied, parsed.data.vehicles, rawVehicles, dirtyKeys);
  }

  const derivedPrString = prStringFromActiveEntry(
    rawRecord,
    'vehicles' in rawRecord ? parsed.data.vehicles : undefined,
    applied,
    dirtyKeys
  );
  if (derivedPrString) {
    applied.prString = derivedPrString;
    reconcileEntryGallery(applied, derivedPrString);
  }

  // Never keep a gallery belonging to a car the session has moved away from (AVS-4399).
  //
  // A payload can announce a new configuration while carrying no `carImages` at all — an older
  // sender drops it in JSON.stringify, and the per-key dirty skip above can remove it from a
  // payload whose `prString` still applies. Either way the receiver was left rendering the
  // PREVIOUS car's images under the new car's identity. Belt-and-braces against the outgoing
  // normalisation in `updatePublicSessionNow`, which fixes the sender for current clients.
  // Runs ONLY when the payload actually announces a configuration. Falling back to live state for
  // both sides made this fire on payloads carrying neither key: the server strips `prString` from
  // every broadcast unless `updateEverything` is set, so most broadcasts took that path and a
  // message as unrelated as `{ micMuted: true }` could wipe the dealer's gallery mid-fetch. It also
  // put a key into `applied` for a genuine no-op, which flips `hasChanges` and fires a duplicate
  // `fetchImages()`.
  let galleryDropped = false;

  if ('prString' in applied) {
    const nextPrString = applied.prString;
    const gallery = ('carImages' in applied ? applied.carImages : userSession().get('carImages')) as
      { prString?: string } | undefined;
    if (gallery && nextPrString && gallery.prString !== nextPrString) {
      logger.info('[Session] Dropping a gallery that belongs to a different configuration', {
        gallery: gallery.prString,
        session: nextPrString,
      });
      applied.carImages = undefined;
      // A locally-dirty gallery is abandoned here too, and its marker goes with it. The dirty-key
      // skip exists to stop STALE remote data clobbering an in-flight edit, but a new car identity
      // is not stale, it supersedes: the gallery belongs to the configuration we just left. Keeping
      // the marker would let the surviving debounced full PUT rebuild from the mixed live state and
      // persist the wrong-car pairing, and clear the marker itself on success.
      clearDirty(['carImages']);
      galleryDropped = true;
    }
  }

  // Track the version even for a no-op payload: we have still observed that server state.
  seedRemoteVersion(incomingVersion);

  const hasChanges = Object.keys(applied).length > 0;

  // One record per inbound payload carrying every gate's verdict — the version gate, the schema
  // strip, the dirty-key skip and the voipMasters generation merge — so "the dealer's value never
  // arrived" and "it arrived and was refused" stop looking identical in a timeline.
  reportRemoteApply(hasChanges ? 'in:applied' : 'in:noop', {
    incomingVersion,
    lastSeenVersion,
    writer,
    appliedKeys: Object.keys(applied),
    dirtySkipped,
    // Keys the payload carried that the schema threw away. `version` always appears here; anything
    // else is a client and a server that disagree about the session shape.
    stripped: isRemoteApplyRecorded() ? Object.keys(rawRecord).filter((key) => !(key in parsed.data)) : undefined,
    galleryDropped,
    derivedPrString: !!derivedPrString,
    voip: voipMerge ? { merged: 'voipMasters' in applied, ownSlotDefended: voipMerge.ownSlotDefended } : undefined,
  });

  if (derivedPrString) {
    // Before the write, so the prString change listener sees the derived car and keeps the hold.
    holdDeckPrString(userSession().get('prString'), derivedPrString);
  }
  if (hasChanges) {
    // Apply under the remote-apply guard so `syncToServer` treats this as inbound state, not a
    // local edit — otherwise it would mark these keys dirty and the next broadcast for them would
    // be self-skipped by the dirty-key gate (AVS-4330).
    runApplyingRemote(() => userSession().setValues(applied as Partial<IPublicSession>));
  }
  if (voipMerge?.ownSlotDefended) {
    repushVoipMasters();
  }
  return hasChanges;
}
